import { useState, useEffect, useMemo, useCallback } from "react";
import { GeoCell } from "@/components/GeoCell";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Undo2, Download, RefreshCw, ChevronRight, ChevronDown, ShieldAlert,
  Search, FileSpreadsheet, Clock, Check, X,
} from "lucide-react";

const ACTION_LABELS = {
  generic_learn_undo: { label: "Undo Learned Generic", color: "bg-amber-100 text-amber-700 border-amber-200" },
  create_user: { label: "Create User", color: "bg-green-100 text-green-700 border-green-200" },
  update_user: { label: "Edit User", color: "bg-blue-100 text-blue-700 border-blue-200" },
  delete_user: { label: "Delete User", color: "bg-red-100 text-red-700 border-red-200" },
  approve_user: { label: "Approve User", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  reject_user: { label: "Reject User", color: "bg-rose-100 text-rose-700 border-rose-200" },
  update_panel_access: { label: "Update Permissions", color: "bg-purple-100 text-purple-700 border-purple-200" },
  reset_test_data: { label: "Reset Test Data", color: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200" },
  change_password: { label: "Change Password", color: "bg-amber-100 text-amber-700 border-amber-200" },
  bulk_approve: { label: "Bulk Approve", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  bulk_delete: { label: "Bulk Delete", color: "bg-red-100 text-red-700 border-red-200" },
  bulk_reject: { label: "Bulk Reject", color: "bg-rose-100 text-rose-700 border-rose-200" },
  bulk_move_team: { label: "Bulk Move Team", color: "bg-amber-100 text-amber-700 border-amber-200" },
  revert: { label: "Revert Action", color: "bg-slate-200 text-slate-800 border-slate-300" },
};

const fmtWhen = (ts) => {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h ago`;
    return d.toLocaleString();
  } catch { return ts; }
};

const jsonPreview = (v, cap = 120) => {
  if (v == null) return <span className="text-slate-400 italic">—</span>;
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return <code className="text-[11px] break-all">{s.length > cap ? s.slice(0, cap) + "…" : s}</code>;
};

const FragmentRow = ({ entry: e, isOpen, meta, targetLabel, onToggle, onRevert }) => (
  <>
    <tr
      className="border-b hover:bg-slate-50 cursor-pointer"
      onClick={() => onToggle(e.id)}
      data-testid={`audit-row-${e.id}`}
    >
      <td className="px-3 py-2">
        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" />
                : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </td>
      <td className="px-3 py-2 text-xs text-slate-600" title={e.timestamp}>
        {fmtWhen(e.timestamp)}
      </td>
      <td className="px-3 py-2">
        <div className="text-slate-800 text-xs font-medium">{e.actor_name || e.actor_email}</div>
        <div className="text-slate-400 text-[11px]">{e.actor_email}</div>
      </td>
      <td className="px-3 py-2">
        <Badge className={`${meta.color} border text-[11px]`}>{meta.label}</Badge>
      </td>
      <td className="px-3 py-2 text-xs text-slate-700">{targetLabel}</td>
      <td className="px-3 py-2 text-xs" data-testid={`audit-geo-${e.id}`}>
        <GeoCell geo={e.actor_geo} ip={e.actor_ip} />
        {e.actor_ip && <div className="text-[10px] text-slate-400 font-mono">{e.actor_ip}{e.actor_ip_guess ? " (session)" : ""}</div>}
      </td>
      <td className="px-3 py-2 text-center">
        {e.reverted
          ? <Badge className="bg-slate-200 text-slate-700 text-[10px]" title={`by ${e.reverted_by}`}>Reverted</Badge>
          : <span className="text-slate-300 text-xs">—</span>}
      </td>
      <td className="px-3 py-2 text-right">
        {e.revertible && !e.reverted && e.action !== "revert" ? (
          <Button
            size="sm" variant="outline"
            className="h-7 px-2 text-[11px] text-purple-700 border-purple-200 hover:bg-purple-50"
            onClick={(ev) => { ev.stopPropagation(); onRevert(e); }}
            data-testid={`audit-revert-${e.id}`}
          >
            <Undo2 className="w-3 h-3 mr-1" /> Revert
          </Button>
        ) : (
          <span className="text-slate-300 text-xs">{e.reverted ? "" : "N/A"}</span>
        )}
      </td>
    </tr>
    {isOpen && (
      <tr className="border-b bg-slate-50">
        <td colSpan={8} className="px-6 py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 mb-1">BEFORE</p>
              <div className="bg-white rounded p-2 border border-slate-200">
                {jsonPreview(e.before, 500)}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 mb-1">AFTER</p>
              <div className="bg-white rounded p-2 border border-slate-200">
                {jsonPreview(e.after, 500)}
              </div>
            </div>
            {e.extra && Object.keys(e.extra).length > 0 && (
              <div className="md:col-span-2">
                <p className="text-[11px] font-semibold text-slate-500 mb-1">EXTRA</p>
                <div className="bg-white rounded p-2 border border-slate-200">
                  {jsonPreview(e.extra, 500)}
                </div>
              </div>
            )}
            {e.target_user_ids && e.target_user_ids.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-[11px] font-semibold text-slate-500 mb-1">TARGET USERS ({e.target_user_ids.length})</p>
                <div className="text-[11px] text-slate-600 font-mono break-all">
                  {e.target_user_ids.join(", ")}
                </div>
              </div>
            )}
          </div>
        </td>
      </tr>
    )}
  </>
);

const AuditLogPanel = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [actionFilter, setActionFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [revertDialog, setRevertDialog] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [includeTest, setIncludeTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, limit: 500, include_test: includeTest };
      if (actionFilter !== "all") params.action = actionFilter;
      const r = await axios.get(`${API}/admin/audit-log`, { headers: getAuthHeader(), params });
      setEntries(r.data.entries || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [days, actionFilter, includeTest]);

  useEffect(() => { load(); }, [load]);

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(e =>
      (e.actor_email || "").toLowerCase().includes(q) ||
      (e.actor_name || "").toLowerCase().includes(q) ||
      (e.target_user_email || "").toLowerCase().includes(q) ||
      (e.target_user_name || "").toLowerCase().includes(q) ||
      (e.action || "").toLowerCase().includes(q)
    );
  }, [entries, searchQuery]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (actionFilter !== "all") params.set("action", actionFilter);
      const r = await axios.get(`${API}/admin/audit-log/export.xlsx?${params.toString()}`, {
        headers: getAuthHeader(), responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `admin_audit_log_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const handleRevert = async () => {
    if (!revertDialog) return;
    setReverting(true);
    try {
      const r = await axios.post(
        `${API}/admin/audit-log/${revertDialog.id}/revert`,
        {},
        { headers: getAuthHeader() },
      );
      toast.success(`${r.data.message} — ${r.data.affected_users} user(s) restored`);
      setRevertDialog(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    } finally {
      setReverting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="audit-log-panel">
      {/* Toolbar */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <ShieldAlert className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-sm font-semibold text-slate-800">Admin Audit Log</p>
                <p className="text-xs text-slate-500">
                  Every admin action on user accounts (auto-purged after 365 days)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search actor / target / action…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-64 h-9 text-sm"
                  data-testid="audit-search-input"
                />
              </div>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-32 h-9 text-sm" data-testid="audit-days-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24h</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-44 h-9 text-sm" data-testid="audit-action-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {Object.entries(ACTION_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="audit-include-test">
                <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />
                Show test accounts
              </label>
              <Button
                variant="outline" size="sm" onClick={load}
                className="h-9" data-testid="audit-refresh"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                size="sm" onClick={handleExport}
                className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="audit-export"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                Export Excel
              </Button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            Showing <strong className="text-slate-700">{filteredEntries.length}</strong> of <strong>{total}</strong> entries
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-sm">
        <CardContent className="p-0">
          {filteredEntries.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="audit-empty">
              {loading ? "Loading…" : "No audit entries match the current filters."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 w-8"></th>
                    <th className="text-left px-3 py-2 w-32">When</th>
                    <th className="text-left px-3 py-2">Actor</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">Target</th>
                    <th className="text-left px-3 py-2">Location</th>
                    <th className="text-center px-3 py-2 w-16">Reverted</th>
                    <th className="text-right px-3 py-2 w-24">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((e) => {
                    const isOpen = expanded.has(e.id);
                    const meta = ACTION_LABELS[e.action] || { label: e.action, color: "bg-slate-100 text-slate-700 border-slate-200" };
                    const targetLabel = e.target_user_email
                      || (e.target_user_ids && e.target_user_ids.length ? `${e.target_user_ids.length} users` : "—");
                    return (
                      <FragmentRow key={e.id} entry={e} isOpen={isOpen} meta={meta} targetLabel={targetLabel}
                                   onToggle={toggleExpand} onRevert={setRevertDialog} />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revert confirmation dialog */}
      <Dialog open={!!revertDialog} onOpenChange={(o) => { if (!o) setRevertDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-purple-600" />
              Revert this action?
            </DialogTitle>
            <DialogDescription>
              This will restore the target user(s) to the exact state they were in
              before this action. The revert itself will be logged as a new audit entry.
            </DialogDescription>
          </DialogHeader>
          {revertDialog && (
            <div className="space-y-2 text-sm py-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Action:</span>
                <Badge className={(ACTION_LABELS[revertDialog.action] || {}).color}>
                  {(ACTION_LABELS[revertDialog.action] || {}).label || revertDialog.action}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Target:</span>
                <span className="font-medium">
                  {revertDialog.target_user_email
                    || `${(revertDialog.target_user_ids || []).length} users`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">When:</span>
                <span className="text-xs">{new Date(revertDialog.timestamp).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Originally by:</span>
                <span className="text-xs">{revertDialog.actor_email}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevertDialog(null)} disabled={reverting}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={handleRevert}
              disabled={reverting}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="audit-revert-confirm"
            >
              <Check className="w-4 h-4 mr-1" />
              {reverting ? "Reverting…" : "Confirm Revert"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogPanel;
